import { Calendar } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import CalendarContextMenu from '../utils/CalendarContextMenu';

export default class BackofficeMoePlanningsIndex {
  constructor() {
    this.calendar = null;
    this.currentMoeId = null;
    this.pendingDeleteSlotId = null;
    this.pendingEditSlotId = null;
    this.pendingEditDate = null;
    this.pendingEditOffset = null;
    this.$container = $('#moe-planning-calendar-container');
    this.$selector = $('#moe-planning-selector');
    this.$noSelection = $('#moe-planning-no-selection');

    this.contextMenu = new CalendarContextMenu();
    this.initMoeSelector();
    this.initDeleteModal();
    this.initEditModal();

    const initialMoeId = this.$selector.val();
    if (initialMoeId) {
      this.currentMoeId = initialMoeId;
      this.initCalendar();
    }
  }

  initMoeSelector() {
    if (this.$selector.is('select')) {
      this.$selector.select2({ width: '100%' });
    }

    this.$selector.on('change', () => {
      const moeId = this.$selector.val();
      if (moeId) {
        this.currentMoeId = moeId;
        this.$container.show();
        this.$noSelection.hide();

        if (this.calendar) {
          this.calendar.destroy();
        }
        this.initCalendar();
      } else {
        this.currentMoeId = null;
        this.$container.hide();
        this.$noSelection.show();

        if (this.calendar) {
          this.calendar.destroy();
          this.calendar = null;
        }
      }
    });
  }

  initCalendar() {
    const calendarEl = document.getElementById('moe-planning-calendar');
    if (!calendarEl) {
      return;
    }

    this.calendar = new Calendar(calendarEl, {
      plugins: [dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin],
      initialView: 'dayGridMonth',
      locale: 'fr',
      timeZone: 'Europe/Paris',
      headerToolbar: {
        left: 'prev,next today',
        center: 'title',
        right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek'
      },
      buttonText: {
        today: "Aujourd'hui",
        month: 'Mois',
        week: 'Semaine',
        day: 'Jour',
        list: 'Liste'
      },
      slotMinTime: '07:00:00',
      slotMaxTime: '20:00:00',
      allDaySlot: false,
      selectable: true,
      editable: true,
      eventResizableFromStart: true,
      height: 'auto',

      events: (info, successCallback, failureCallback) => {
        this.fetchEvents(info.startStr, info.endStr, successCallback, failureCallback);
      },

      select: (info) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (new Date(info.startStr) < today) return;
        if (info.allDay) {
          this.createBulkDaySlots(info.startStr, info.endStr);
        } else {
          this.createSlot(info.startStr, info.endStr);
        }
      },

      eventClick: (info) => {
        const props = info.event.extendedProps;
        if (props.pastDay) return;
        if (props.type === 'availability') {
          this.promptEditSlot(props.slot_id, info.event.startStr, info.event.endStr);
        }
      },

      eventDrop: (info) => {
        const props = info.event.extendedProps;
        if (props.pastDay) { info.revert(); return; }
        if (props.type === 'availability') {
          this.updateSlot(props.slot_id, info.event.startStr, info.event.endStr);
        } else {
          info.revert();
        }
      },

      eventResize: (info) => {
        const props = info.event.extendedProps;
        if (props.pastDay) { info.revert(); return; }
        if (props.type === 'availability') {
          this.updateSlot(props.slot_id, info.event.startStr, info.event.endStr);
        } else {
          info.revert();
        }
      },

      eventDidMount: this.contextMenu.getEventDidMount((_actionExecutionId, _message) => {
        this.calendar.refetchEvents();
      })
    });

    this.calendar.render();
  }

  fetchEvents(start, end, successCallback, failureCallback) {
    const url = this.buildUrl('events-url', { '__MOE_ID__': this.currentMoeId });

    $.ajax({
      url: url,
      method: 'GET',
      data: { start: start, end: end },
      headers: { 'Accept': 'application/json' },
      success: (data) => { successCallback(data.map(e => this._markIfPastDay(e))); },
      error: (xhr) => {
        console.error('Failed to fetch events:', xhr.responseText);
        failureCallback(xhr);
      }
    });
  }

  createSlot(startsAt, endsAt) {
    const url = this.buildUrl('create-slot-url', { '__MOE_ID__': this.currentMoeId });
    const token = $('meta[name="csrf-token"]').attr('content');

    $.ajax({
      url: url,
      method: 'POST',
      headers: { 'X-CSRF-Token': token, 'Accept': 'application/json' },
      contentType: 'application/json',
      data: JSON.stringify({ slot: { starts_at: startsAt, ends_at: endsAt } }),
      success: (data) => {
        if (data.success) {
          this.calendar.refetchEvents();
          this.showSuccess(data.message);
        }
      },
      error: (xhr) => {
        const resp = xhr.responseJSON;
        this.showError(resp ? resp.error : 'Erreur');
      }
    });
  }

  createBulkDaySlots(startStr, endStr) {
    const url = this.buildUrl('bulk-slots-url', { '__MOE_ID__': this.currentMoeId });
    const token = $('meta[name="csrf-token"]').attr('content');

    const dates = [];
    const current = new Date(`${startStr}T12:00:00Z`);
    const end = new Date(`${endStr}T12:00:00Z`);

    while (current < end) {
      const y = current.getUTCFullYear();
      const m = String(current.getUTCMonth() + 1).padStart(2, '0');
      const d = String(current.getUTCDate()).padStart(2, '0');
      dates.push(`${y}-${m}-${d}`);
      current.setUTCDate(current.getUTCDate() + 1);
    }

    $.ajax({
      url: url,
      method: 'POST',
      headers: { 'X-CSRF-Token': token, 'Accept': 'application/json' },
      contentType: 'application/json',
      data: JSON.stringify({ dates: dates }),
      success: (data) => {
        if (data.success) {
          this.calendar.refetchEvents();
          if (data.created === 0 && data.skipped > 0) {
            this.showWarning('Ce créneau est déjà planifié.');
          } else {
            this.showSuccess(data.message);
          }
        }
      },
      error: (xhr) => {
        const resp = xhr.responseJSON;
        this.showError(resp ? resp.error : 'Erreur');
      }
    });
  }

  updateSlot(slotId, startsAt, endsAt) {
    const url = this.buildUrl('update-slot-url', {
      '__MOE_ID__': this.currentMoeId,
      '__SLOT_ID__': slotId
    });
    const token = $('meta[name="csrf-token"]').attr('content');

    $.ajax({
      url: url,
      method: 'PUT',
      headers: { 'X-CSRF-Token': token, 'Accept': 'application/json' },
      contentType: 'application/json',
      data: JSON.stringify({ slot: { starts_at: startsAt, ends_at: endsAt } }),
      success: (data) => {
        if (data.success) {
          this.calendar.refetchEvents();
          this.showSuccess(data.message);
        }
      },
      error: (xhr) => {
        this.calendar.refetchEvents();
        const resp = xhr.responseJSON;
        this.showError(resp ? resp.error : 'Erreur');
      }
    });
  }

  promptEditSlot(slotId, startStr, endStr) {
    const startMatch = startStr && startStr.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}):\d{2}([-+]\d{2}:\d{2}|Z)/);
    const endMatch   = endStr   && endStr.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}):\d{2}([-+]\d{2}:\d{2}|Z)/);

    if (!startMatch || !endMatch) {
      return;
    }

    this.pendingEditSlotId = slotId;
    this.pendingEditDate   = startMatch[1];
    this.pendingEditOffset = startMatch[3] === 'Z' ? '+00:00' : startMatch[3];

    const dateObj = new Date(startStr);
    const formatted = dateObj.toLocaleDateString('fr-FR', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
      timeZone: 'Europe/Paris'
    });

    $('#edit-slot-date').text(formatted);
    $('#edit-slot-start').val(startMatch[2]);
    $('#edit-slot-end').val(endMatch[2]);
    $('#editSlotModal').modal('show');
  }

  promptDeleteSlot(slotId) {
    this.pendingDeleteSlotId = slotId;
    $('#deleteSlotModal').modal('show');
  }

  deleteSlot(slotId) {
    const url = this.buildUrl('destroy-slot-url', {
      '__MOE_ID__': this.currentMoeId,
      '__SLOT_ID__': slotId
    });
    const token = $('meta[name="csrf-token"]').attr('content');

    $.ajax({
      url: url,
      method: 'DELETE',
      headers: { 'X-CSRF-Token': token, 'Accept': 'application/json' },
      success: (data) => {
        if (data.success) {
          this.calendar.refetchEvents();
          this.showSuccess(data.message);
        }
      },
      error: (xhr) => {
        const resp = xhr.responseJSON;
        this.showError(resp ? resp.error : 'Erreur');
      }
    });
  }

  initDeleteModal() {
    $('#confirm-delete-slot').on('click', () => {
      if (this.pendingDeleteSlotId) {
        this.deleteSlot(this.pendingDeleteSlotId);
        this.pendingDeleteSlotId = null;
      }
      $('#deleteSlotModal').modal('hide');
    });
  }

  initEditModal() {
    $('#confirm-edit-slot-btn').on('click', () => {
      const startTime = $('#edit-slot-start').val();
      const endTime   = $('#edit-slot-end').val();

      if (!startTime || !endTime || !this.pendingEditDate || !this.pendingEditSlotId) {
        return;
      }

      const startsAt = `${this.pendingEditDate}T${startTime}:00${this.pendingEditOffset}`;
      const endsAt   = `${this.pendingEditDate}T${endTime}:00${this.pendingEditOffset}`;

      this.updateSlot(this.pendingEditSlotId, startsAt, endsAt);
      $('#editSlotModal').modal('hide');
    });

    $('#edit-slot-delete-btn').on('click', () => {
      const slotId = this.pendingEditSlotId;
      $('#editSlotModal').modal('hide');
      this.promptDeleteSlot(slotId);
    });
  }

  buildUrl(dataAttr, replacements) {
    let url = this.$container.data(dataAttr);
    for (const [placeholder, value] of Object.entries(replacements)) {
      url = url.replace(placeholder, value);
    }
    return url;
  }

  showSuccess(message) {
    $.toast({
      heading: 'Succès',
      text: message,
      icon: 'success',
      position: 'top-right',
      loader: true,
      loaderBg: '#28a745'
    });
  }

  showWarning(message) {
    $.toast({
      heading: 'Attention',
      text: message,
      icon: 'warning',
      position: 'top-right',
      loader: true,
      loaderBg: '#da8609'
    });
  }

  showError(message) {
    $.toast({
      heading: 'Erreur',
      text: message,
      icon: 'error',
      position: 'top-right',
      loader: true,
      loaderBg: '#dc3545'
    });
  }

  _markIfPastDay(event) {
    if (event.extendedProps?.type !== 'availability') return event;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (new Date(event.start) >= today) return event;
    return { ...event, color: '#f0ad4e', title: 'Date passée', extendedProps: { ...event.extendedProps, pastDay: true } };
  }

  onDestroy() {
    if (this.calendar) {
      this.calendar.destroy();
      this.calendar = null;
    }
  }
}
